<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Bildirim Logları</span>
            <div>
              <v-btn color="primary" @click="refreshLogs" :loading="loading" class="mr-2">
                <v-icon left>mdi-refresh</v-icon>
                Yenile
              </v-btn>
            </div>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card elevation="4" class="notification-logs-card">
          <!-- İstatistikler -->
          <v-card-text v-if="stats">
            <v-row>
              <v-col cols="12" md="3">
                <v-card color="blue lighten-4" class="pa-3">
                  <div class="text-h6">{{ stats.summary.total }}</div>
                  <div class="text-caption">Toplam Bildirim</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="green lighten-4" class="pa-3">
                  <div class="text-h6">{{ stats.summary.successful }}</div>
                  <div class="text-caption">Başarılı</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="red lighten-4" class="pa-3">
                  <div class="text-h6">{{ stats.summary.failed }}</div>
                  <div class="text-caption">Başarısız</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card color="orange lighten-4" class="pa-3">
                  <div class="text-h6">{{ stats.summary.successRate }}%</div>
                  <div class="text-caption">Başarı Oranı</div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Filtreler -->
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-select v-model="filters.datalogger_id" :items="dataloggerOptions" item-title="name" item-value="id"
                  label="Datalogger" clearable outlined dense @update:model-value="applyFilters"></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select v-model="filters.message_type" :items="messageTypeOptions" item-title="title"
                  item-value="value" label="Mesaj Tipi" clearable outlined dense
                  @update:model-value="applyFilters"></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select v-model="filters.notification_reason" :items="notificationReasonOptions" item-title="title"
                  item-value="value" label="Bildirim Nedeni" clearable outlined dense
                  @update:model-value="applyFilters"></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select v-model="filters.status" :items="statusOptions" label="Durum" clearable outlined dense
                  @update:model-value="applyFilters"></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field v-model="filters.start_date" type="date" label="Başlangıç Tarihi" outlined dense
                  @update:model-value="applyFilters"></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field v-model="filters.end_date" type="date" label="Bitiş Tarihi" outlined dense
                  @update:model-value="applyFilters"></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-text>

            <!-- Alert -->
            <v-alert v-if="alert.show" :type="alert.type" dismissible @input="alert.show = false" class="mb-4">
              {{ alert.message }}
            </v-alert>

            <!-- Tablo -->
            <v-data-table :headers="headers" :items="logs" :loading="loading"
              class="elevation-2 notification-logs-table" :items-per-page="pagination.itemsPerPage"
              :server-items-length="pagination.totalItems" @update:options="handleTableUpdate">
              <template v-slot:item.datalogger_name="{ item }">
                <v-chip small color="primary" text-color="white">
                  {{ item.datalogger_name }}
                </v-chip>
              </template>

              <template v-slot:item.message_type="{ item }">
                <v-chip small :color="getMessageTypeColor(item.message_type)" text-color="white">
                  {{ formatMessageType(item.message_type) }}
                </v-chip>
              </template>

              <template v-slot:item.notification_reason="{ item }">
                <v-chip small :color="getNotificationReasonColor(item.notification_reason)" text-color="white">
                  <v-icon left small>
                    {{ item.notification_reason === 'recovery' ? 'mdi-arrow-up-circle' : 'mdi-arrow-down-circle' }}
                  </v-icon>
                  {{ formatNotificationReason(item.notification_reason) }}
                </v-chip>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip small :color="item.status === 'sent' ? 'success' : 'error'" text-color="white">
                  <v-icon left small>
                    {{ item.status === 'sent' ? 'mdi-check' : 'mdi-close' }}
                  </v-icon>
                  {{ item.status === 'sent' ? 'Gönderildi' : 'Başarısız' }}
                </v-chip>
              </template>

              <template v-slot:item.channel="{ item }">
                <v-chip small :color="getChannelColor(item.channel)" text-color="white">
                  {{ formatChannel(item.channel) }}
                </v-chip>
              </template>

              <template v-slot:item.minutes_without_message="{ item }">
                <span class="font-weight-bold">{{ item.minutes_without_message }} dk</span>
              </template>

              <template v-slot:item.recipients="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ props }">
                    <v-chip small color="info" text-color="white" v-bind="props">
                      {{ item.recipients.length }} alıcı
                    </v-chip>
                  </template>
                  <div>
                    <div v-for="email in item.recipients" :key="email">
                      {{ email }}
                    </div>
                  </div>
                </v-tooltip>
              </template>

              <template v-slot:item.sent_at="{ item }">
                <div>
                  <div>{{ formatDate(item.sent_at) }}</div>
                  <div class="text-caption text--secondary">
                    {{ formatTime(item.sent_at) }}
                  </div>
                </div>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn icon small @click="viewDetails(item)" color="primary">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
                <v-btn icon small @click="deleteLog(item)" color="error">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Detay Dialog -->
    <v-dialog v-model="detailDialog" max-width="600px">
      <v-card v-if="selectedLog">
        <v-card-title>
          <span class="text-h6">Bildirim Detayları</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <strong>Datalogger:</strong> {{ selectedLog.datalogger_name }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Mesaj Tipi:</strong> {{ formatMessageType(selectedLog.message_type) }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Bildirim Nedeni:</strong>
              <v-chip small :color="getNotificationReasonColor(selectedLog.notification_reason)" text-color="white" class="ml-2">
                <v-icon left small>
                  {{ selectedLog.notification_reason === 'recovery' ? 'mdi-arrow-up-circle' : 'mdi-arrow-down-circle' }}
                </v-icon>
                {{ formatNotificationReason(selectedLog.notification_reason) }}
              </v-chip>
            </v-col>
            <v-col cols="12" md="6">
              <strong>Durum:</strong>
              <v-chip small :color="selectedLog.status === 'sent' ? 'success' : 'error'" text-color="white"
                class="ml-2">
                {{ selectedLog.status === 'sent' ? 'Gönderildi' : 'Başarısız' }}
              </v-chip>
            </v-col>
            <v-col cols="12" md="6">
              <strong>Kanal:</strong>
              <v-chip small :color="getChannelColor(selectedLog.channel)" text-color="white" class="ml-2">
                {{ formatChannel(selectedLog.channel) }}
              </v-chip>
            </v-col>
            <v-col cols="12" md="6">
              <strong>Mesajsız Süre:</strong> {{ selectedLog.minutes_without_message }} dakika
            </v-col>
            <v-col cols="12">
              <strong>Gönderim Tarihi:</strong> {{ formatDateTime(selectedLog.sent_at) }}
            </v-col>
            <v-col cols="12">
              <strong>Alıcılar:</strong>
              <v-chip v-for="email in selectedLog.recipients" :key="email" small class="ma-1" color="info"
                text-color="white">
                {{ email }}
              </v-chip>
            </v-col>
            <v-col cols="12" v-if="selectedLog.error_message">
              <strong>Hata Mesajı:</strong>
              <v-alert type="error" class="mt-2">
                {{ selectedLog.error_message }}
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Silme Onay Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>
          <span class="text-h6">Silme Onayı</span>
        </v-card-title>
        <v-card-text>
          Bu bildirim logunu silmek istediğinizden emin misiniz?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">İptal</v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="deleting">Sil</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'NotificationLogs',
  data() {
    return {
      logs: [],
      stats: null,
      loading: false,
      deleting: false,
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25
      },
      filters: {
        datalogger_id: null,
        message_type: null,
        notification_reason: null,
        status: null,
        start_date: null,
        end_date: null
      },
      dataloggerOptions: [],
      messageTypeOptions: [
        { title: 'Realtime', value: 'realtime' },
        { title: 'Save', value: 'save' },
        { title: 'Synced', value: 'synced' },
        { title: 'History', value: 'history' },
        { title: 'History Multi', value: 'history-multi' },
        { title: 'History Synced', value: 'history-synced' },
        { title: 'History Multi Synced', value: 'history-multi-synced' }
      ],
      notificationReasonOptions: [
        { title: 'OFFLINE', value: 'down' },
        { title: 'ONLINE', value: 'recovery' }
      ],
      statusOptions: [
        { title: 'Gönderildi', value: 'sent' },
        { title: 'Başarısız', value: 'failed' }
      ],
      alert: {
        show: false,
        type: 'success',
        message: ''
      },
      detailDialog: false,
      deleteDialog: false,
      selectedLog: null,
      logToDelete: null,
      headers: [
        { text: 'Datalogger', value: 'datalogger_name', sortable: false },
        { text: 'Mesaj Tipi', value: 'message_type', sortable: false },
        { text: 'Bildirim Nedeni', value: 'notification_reason', sortable: false },
        { text: 'Durum', value: 'status', sortable: false },
        { text: 'Kanal', value: 'channel', sortable: false },
        { text: 'Mesajsız Süre', value: 'minutes_without_message', sortable: false },
        { text: 'Alıcılar', value: 'recipients', sortable: false },
        { text: 'Gönderim Tarihi', value: 'sent_at', sortable: false },
        { text: 'İşlemler', value: 'actions', sortable: false }
      ]
    }
  },
  async created() {
    await this.fetchDataloggers()
    await this.fetchLogs()
    await this.fetchStats()
  },
  methods: {
    async fetchLogs() {
      this.loading = true
      try {
        const params = new URLSearchParams({
          page: this.pagination.currentPage,
          limit: this.pagination.itemsPerPage
        })

        // Filtreleri ekle
        Object.keys(this.filters).forEach(key => {
          if (this.filters[key]) {
            params.append(key, this.filters[key])
          }
        })

        const response = await this.$api.get(`/api/notification-logs?${params}`)
        this.logs = response.data.logs
        this.pagination = response.data.pagination
      } catch (error) {
        console.error('Error fetching notification logs:', error)
        this.showAlert('error', 'Bildirim logları yüklenirken hata oluştu')
      } finally {
        this.loading = false
      }
    },

    async fetchStats() {
      try {
        const params = new URLSearchParams()
        if (this.filters.start_date) params.append('startDate', this.filters.start_date)
        if (this.filters.end_date) params.append('endDate', this.filters.end_date)

        const response = await this.$api.get(`/api/notification-logs/stats/summary?${params}`)
        this.stats = response.data
      } catch (error) {
        console.error('Error fetching stats:', error)
      }
    },

    async fetchDataloggers() {
      try {
        const response = await this.$api.get('/api/servers')
        this.dataloggerOptions = response.data.map(server => ({
          id: server.id,
          name: server.name
        }))
      } catch (error) {
        console.error('Error fetching dataloggers:', error)
      }
    },

    async refreshLogs() {
      await this.fetchLogs()
      await this.fetchStats()
    },

    async applyFilters() {
      this.pagination.currentPage = 1
      await this.fetchLogs()
      await this.fetchStats()
    },

    async handleTableUpdate(options) {
      this.pagination.currentPage = options.page
      this.pagination.itemsPerPage = options.itemsPerPage
      await this.fetchLogs()
    },

    viewDetails(log) {
      this.selectedLog = log
      this.detailDialog = true
    },

    deleteLog(log) {
      this.logToDelete = log
      this.deleteDialog = true
    },

    async confirmDelete() {
      this.deleting = true
      try {
        await this.$api.delete(`/api/notification-logs/${this.logToDelete.id}`)
        this.showAlert('success', 'Bildirim logu başarıyla silindi')
        await this.fetchLogs()
        await this.fetchStats()
      } catch (error) {
        console.error('Error deleting log:', error)
        this.showAlert('error', 'Bildirim logu silinirken hata oluştu')
      } finally {
        this.deleting = false
        this.deleteDialog = false
        this.logToDelete = null
      }
    },

    getMessageTypeColor(messageType) {
      const colors = {
        'realtime': 'green',
        'save': 'blue',
        'synced': 'purple',
        'history': 'orange',
        'history-multi': 'deep-orange',
        'history-synced': 'indigo',
        'history-multi-synced': 'red'
      }
      return colors[messageType] || 'grey'
    },

    formatMessageType(messageType) {
      const types = {
        'realtime': 'Realtime',
        'save': 'Save',
        'synced': 'Synced',
        'history': 'History',
        'history-multi': 'History Multi',
        'history-synced': 'History Synced',
        'history-multi-synced': 'History Multi Synced'
      }
      return types[messageType] || messageType
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('tr-TR')
    },

    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString('tr-TR')
    },

    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString('tr-TR')
    },

    getNotificationReasonColor(reason) {
      return reason === 'recovery' ? 'success' : 'error'
    },

    formatNotificationReason(reason) {
      const reasons = {
        'down': 'OFFLINE',
        'recovery': 'ONLINE'
      }
      return reasons[reason] || reason
    },

    getChannelColor(channel) {
      if (!channel) return 'green'
      if (channel.includes(',')) return 'purple'
      if (channel === 'telegram') return 'blue'
      return 'green'
    },

    formatChannel(channel) {
      if (!channel || channel === 'email') return 'Email'
      if (channel === 'telegram') return 'Telegram'
      if (channel.includes(',')) return 'Email + Telegram'
      return channel
    },

    showAlert(type, message) {
      this.alert = {
        show: true,
        type: type,
        message: message
      }
    }
  }
}
</script>

<style scoped>
/* White Background Card Styles */
.white-bg-card {
  background-color: white !important;
}

.white-bg-title {
  background: white !important;
  color: #2C5F7C !important;
}

.v-theme--dark .white-bg-card {
  background-color: white !important;
}

.v-theme--dark .white-bg-title {
  background: white !important;
  color: #2C5F7C !important;
}

.notification-logs-card {
  border-radius: 12px;
  overflow: hidden;
}

.notification-logs-header {
  padding: 20px 24px;
  font-weight: 600;
}

.notification-logs-header:not(.white-bg-title) {
  background: linear-gradient(135deg, #2C5F7C 0%, #A8D5BA 100%);
  color: white;
}

.notification-logs-table {
  border-radius: 8px;
  overflow: hidden;
}



/* Dark Theme */
.v-theme--dark .notification-logs-header:not(.white-bg-title) {
  background: linear-gradient(135deg, #4A8DB8 0%, #A8D5BA 100%);
}
</style>
